/**
 * modules/welcome/config.js — Pop-up de bienvenida (login / registro).
 *
 * La config vive en `siteConfig.welcome_popup` (JSON editable desde el panel
 * Diseño). `image` guarda una clave de asset local (`assets/images/...`) o una
 * URL directa (Cloudinary); `resolveWelcomeImage` la resuelve a una URL usable.
 */
import shooting0479 from '@/assets/images/shooting-modelos/IMG_0479.JPG'
import shooting0483 from '@/assets/images/shooting-modelos/IMG_0483.JPG'
import shooting0488 from '@/assets/images/shooting-modelos/IMG_0488.JPG'
import shooting0491 from '@/assets/images/shooting-modelos/IMG_0491.JPG'
import shooting0492 from '@/assets/images/shooting-modelos/IMG_0492.JPG'
import shooting0496 from '@/assets/images/shooting-modelos/IMG_0496.JPG'

/** Imágenes de assets ofrecidas en el editor del pop-up. */
export const WELCOME_ASSETS = [
  { key: 'assets/images/shooting-modelos/IMG_0479.JPG', label: 'Shooting grupal', src: shooting0479 },
  { key: 'assets/images/shooting-modelos/IMG_0483.JPG', label: 'Gorra negra', src: shooting0483 },
  { key: 'assets/images/shooting-modelos/IMG_0488.JPG', label: 'Colección', src: shooting0488 },
  { key: 'assets/images/shooting-modelos/IMG_0491.JPG', label: 'Gorra blanca', src: shooting0491 },
  { key: 'assets/images/shooting-modelos/IMG_0492.JPG', label: 'Retrato', src: shooting0492 },
  { key: 'assets/images/shooting-modelos/IMG_0496.JPG', label: 'Look negro', src: shooting0496 },
]

const LEGACY_WELCOME_IMAGES = {
  'assets/images/banner-1.png': shooting0479,
  'assets/images/destacados.png': shooting0488,
  'assets/images/ultimos-drops.png': shooting0492,
  'assets/images/hoodies.png': shooting0496,
  'assets/images/ropa-urbana.png': shooting0491,
  'assets/images/gorras.png': shooting0483,
}

/** Default (mismo shape que `_default_welcome_popup` del backend). */
export const DEFAULT_WELCOME_POPUP = {
  enabled: false,
  delay_seconds: 6,
  frequency: 'once', // once | daily | always
  image: 'assets/images/shooting-modelos/IMG_0479.JPG',
  image_position: 'right', // left | right | top
  image_object_position: 'center', // foco del recorte de la imagen
  show_close: true,
  title: 'Bienvenido a GODÉA',
  subtitle:
    'Accede a tu cuenta para seguir tus pedidos, guardar favoritos y disfrutar de una experiencia personalizada.',
  primary_button_text: 'Iniciar sesión',
  primary_button_action: 'login',
  secondary_button_text: 'Crear cuenta',
  secondary_button_action: 'register',
  translations: {
    en: {
      title: 'Welcome to GODÉA',
      subtitle:
        'Sign in to track your orders, save favorites and enjoy a personalized experience.',
      primary_button_text: 'Sign in',
      secondary_button_text: 'Create account',
    },
  },
}

/** Normaliza la config del backend (puede venir incompleta o null). */
export function normalizeWelcomePopup(raw) {
  const src = raw && typeof raw === 'object' ? raw : {}
  const en = (src.translations && src.translations.en) || {}
  return {
    enabled: Boolean(src.enabled),
    delay_seconds: Number(src.delay_seconds) >= 0 ? Number(src.delay_seconds) : DEFAULT_WELCOME_POPUP.delay_seconds,
    frequency: ['once', 'daily', 'always'].includes(src.frequency) ? src.frequency : DEFAULT_WELCOME_POPUP.frequency,
    image: src.image || DEFAULT_WELCOME_POPUP.image,
    image_position: ['left', 'right', 'top'].includes(src.image_position) ? src.image_position : DEFAULT_WELCOME_POPUP.image_position,
    image_object_position: typeof src.image_object_position === 'string' && src.image_object_position.trim()
      ? src.image_object_position
      : DEFAULT_WELCOME_POPUP.image_object_position,
    show_close: src.show_close !== false,
    title: src.title || DEFAULT_WELCOME_POPUP.title,
    subtitle: src.subtitle || DEFAULT_WELCOME_POPUP.subtitle,
    primary_button_text: src.primary_button_text || DEFAULT_WELCOME_POPUP.primary_button_text,
    primary_button_action: src.primary_button_action === 'register' ? 'register' : 'login',
    secondary_button_text: src.secondary_button_text || DEFAULT_WELCOME_POPUP.secondary_button_text,
    secondary_button_action: src.secondary_button_action === 'login' ? 'login' : 'register',
    translations: {
      en: {
        title: en.title || '',
        subtitle: en.subtitle || '',
        primary_button_text: en.primary_button_text || '',
        secondary_button_text: en.secondary_button_text || '',
      },
    },
  }
}

/** Resuelve `image` (asset local o URL) a una URL usable por <img>. */
export function resolveWelcomeImage(value) {
  if (!value) return ''
  const asset = WELCOME_ASSETS.find((a) => a.key === value)
  if (asset) return asset.src
  if (LEGACY_WELCOME_IMAGES[value]) return LEGACY_WELCOME_IMAGES[value]
  return value
}

/** Clave de localStorage para recordar que el usuario ya vio el pop-up. */
export const WELCOME_POPUP_STORAGE_KEY = 'godea_welcome_popup_seen'
